import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { useSearchParams } from "react-router-dom";

import { GridStack } from "gridstack";
import type { GridItemHTMLElement, GridStackWidget } from "gridstack";

// import "gridstack/dist/gridstack.min.css";

import FooterText from "src/components/footerText";
import AuthSubHeader from "src/components/authSubHeader";

import CoverageAvailable from "./coverageAvailable";
import ActionRequired from "./actionRequired";
import SpecialityPharmacy from "./specialityPharmacy";
import PendingTable from "./pending";

import useFetch from "src/hooks/useFetch";

import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";
import { AuthContext } from "src/context/authContext";

import { APIEndpoints } from "src/types/apiTypes";
import { ToastTypes } from "src/types";

import { ActionDashboardState, TodoResponseType } from "./types";

import {
  initialState,
  tableFilterReducer,
} from "../manufacturer/actionDashboard/reducer";

import {
  ActionStatisticsDashboardActionType,
  PatientFilterStateActions,
} from "../manufacturer/actionDashboard/types";

import { PracticeDropdownResponse } from "src/components/caseStepTwo/types";

/* ============================================================
 * SECTION ORDER
 * ============================================================
 */

const DEFAULT_SECTION_ORDER = [
  "coverage_available",
  "action_required",
  "specialty_pharmacy",
  "pending",
] as const;

type DashboardSectionId = (typeof DEFAULT_SECTION_ORDER)[number];

/* ============================================================
 * TYPES
 * ============================================================
 */

interface DashboardSectionPosition {
  id: DashboardSectionId;
  position: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface GridSectionProps {
  id: DashboardSectionId;
  title: string;
  children: ReactNode;

  /**
   * Controls visibility of everything below the purple section header.
   */
  isCollapsed: boolean;

  /**
   * Called ONLY when collapse arrow is clicked.
   */
  onToggleCollapse: () => void;
}

/* ============================================================
 * HELPERS
 * ============================================================
 */

/**
 * Checks whether a value is a valid dashboard section ID.
 */
const isDashboardSectionId = (value: unknown): value is DashboardSectionId => {
  return (
    typeof value === "string" &&
    DEFAULT_SECTION_ORDER.includes(value as DashboardSectionId)
  );
};

/**
 * Uses API order only if all 4 sections are present.
 */
const resolveSectionOrder = (order?: string[] | null): DashboardSectionId[] => {
  const validOrder = order?.filter(isDashboardSectionId) ?? [];

  return validOrder.length === DEFAULT_SECTION_ORDER.length &&
    new Set(validOrder).size === DEFAULT_SECTION_ORDER.length
    ? validOrder
    : [...DEFAULT_SECTION_ORDER];
};

/**
 * Confirms GridStack instance still owns mounted DOM.
 */
const isGridActive = (grid: GridStack | null): grid is GridStack => {
  return Boolean(grid?.el?.isConnected);
};

/**
 * Gets the current visual order of GridStack sections.
 */
const getSectionPositions = (grid: GridStack): DashboardSectionPosition[] => {
  if (!isGridActive(grid)) {
    return [];
  }

  try {
    return grid
      .getGridItems()
      .map((item) => {
        const id = item.dataset.gridId;

        const node = item.gridstackNode;

        if (!isDashboardSectionId(id) || !node) {
          return null;
        }

        return {
          id,
          position: 0,
          x: node.x ?? 0,
          y: node.y ?? 0,
          w: node.w ?? 1,
          h: node.h ?? 1,
        };
      })
      .filter((item): item is DashboardSectionPosition => item !== null)
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map((item, index) => ({
        ...item,
        position: index + 1,
      }));
  } catch (error) {
    console.error("Unable to read GridStack positions:", error);

    return [];
  }
};

/**
 * Applies section ordering to current GridStack widgets.
 */
const applySectionOrder = (
  grid: GridStack,
  order: DashboardSectionId[],
): void => {
  if (!isGridActive(grid)) {
    return;
  }

  const items = grid.getGridItems();

  const itemsById = new Map<DashboardSectionId, GridItemHTMLElement>();

  items.forEach((item) => {
    const id = item.dataset.gridId;

    if (isDashboardSectionId(id)) {
      itemsById.set(id, item);
    }
  });

  let y = 0;

  const layout = order.reduce<GridStackWidget[]>((widgets, id) => {
    const item = itemsById.get(id);

    if (!item) {
      return widgets;
    }

    const h = item.gridstackNode?.h ?? 1;

    widgets.push({
      id,
      x: 0,
      y,
      w: 1,
      h,
      noResize: true,
      sizeToContent: true,
    });

    y += h;

    return widgets;
  }, []);

  if (layout.length === DEFAULT_SECTION_ORDER.length) {
    grid.load(layout, false);
  }
};

/* ============================================================
 * GRID SECTION
 * ============================================================
 *
 * Important:
 *
 * The collapse button and drag button are separate.
 *
 * Arrow:
 *     collapse / expand
 *
 * Grip:
 *     drag section
 *
 * We DO NOT hide card-body.
 *
 * Instead, we add:
 *
 *     action-dashboard-section-collapsed
 *
 * CSS then hides only tbody.
 * ============================================================
 */

const GridSection: FC<GridSectionProps> = ({
  id,
  title,
  children,
  isCollapsed,
  onToggleCollapse,
}): JSX.Element => {
  return (
    <div
      className="grid-stack-item"
      data-grid-id={id}
      data-gs-id={id}
      data-gs-x="0"
      data-gs-w="1"
      data-gs-h="1"
      data-gs-no-resize="true"
      data-gs-size-to-content="true"
    >
      <div className="grid-stack-item-content">
        <section className="card action-dashboard-grid-card">
          {/* ===============================================
              CARD HEADER - ALWAYS VISIBLE
          =============================================== */}

          <div className="card-header custom-bg-purple d-flex align-items-center justify-content-between gap-3" onClick={(event) => {
                  onToggleCollapse();
                }}>
            <h3 className="text-white mb-0 flex-grow-1">{title}</h3>

            <div className="d-flex align-items-center gap-3">
              {/* =========================================
                  COLLAPSE / EXPAND

                  Only this button changes collapse state.
              ========================================= */}

              <button
                type="button"
                className="btn btn-link text-white p-0 border-0 action-dashboard-collapse-button"
                title={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
                aria-label={
                  isCollapsed
                    ? `Expand ${title} section`
                    : `Collapse ${title} section`
                }
                aria-expanded={!isCollapsed}
                style={{
                  position: "relative",
                  zIndex: 20,
                  pointerEvents: "auto",
                  width: "32px",
                  height: "32px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onToggleCollapse();
                }}
              >
                <i
                  className={`fas ${
                    isCollapsed ? "fa-chevron-down" : "fa-chevron-up"
                  }`}
                  aria-hidden="true"
                />
              </button>

              {/* =========================================
                  DRAG HANDLE

                  Only this button moves the section.
              ========================================= */}

              <button
                type="button"
                className="action-dashboard-drag-handle"
                title={`Move ${title}`}
                aria-label={`Move ${title} section`}
              >
                <i className="fas fa-grip-vertical" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* ===============================================
              BODY

              The purple header above always stays visible.
              Description, table header, table rows, and
              pagination all collapse together.
          =============================================== */}

          {!isCollapsed && <div className="card-body p-0">{children}</div>}
        </section>
      </div>
    </div>
  );
};

/* ============================================================
 * MAIN COMPONENT
 * ============================================================
 */

const ActionDashboardListing: FC = (): JSX.Element => {
  const [queryParams, dispatch] = useReducer(tableFilterReducer, initialState);

  const [, updateSearchParams] = useSearchParams();

  /* ========================================================
   * COLLAPSE STATE
   * ========================================================
   *
   * Every section has its own independent collapse state.
   */

  const [collapsedSections, setCollapsedSections] = useState<
    Record<DashboardSectionId, boolean>
  >({
    coverage_available: false,
    action_required: false,
    specialty_pharmacy: false,
    pending: false,
  });

  /* ========================================================
   * GRID REFS
   * ========================================================
   */

  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  const gridInstanceRef = useRef<GridStack | null>(null);

  const refreshFrameRef = useRef<number | null>(null);

  const secondRefreshFrameRef = useRef<number | null>(null);

  const currentOrderRef = useRef<DashboardSectionId[]>([
    ...DEFAULT_SECTION_ORDER,
  ]);

  const saveOrderRef = useRef<(order: DashboardSectionId[]) => void>(
    () => undefined,
  );

  /* ========================================================
   * CONTEXT
   * ========================================================
   */

  const { showToast } = useContext(ToastContext);

  const { setLoader, hideLoader } = useContext(LoaderContext);

  const { userData } = useContext(AuthContext);

  /* ========================================================
   * INITIAL LOADER
   * ========================================================
   */

  useLayoutEffect(() => {
    setLoader();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ========================================================
   * PRACTICE DROPDOWN
   * ========================================================
   */

  const { data: practiceDropdownApiRes } = useFetch<PracticeDropdownResponse>(
    APIEndpoints.PRACTICE_DROPDOWN,
  );

  /* ========================================================
   * DASHBOARD DATA
   * ========================================================
   */

  const {
    data: dashboardData,
    error: dashboardError,
    mutate: updateDataWithQueryParams,
  } = useFetch<TodoResponseType>(
    APIEndpoints.ACTION_DASHBOARD_DATA,
    { ...queryParams, start_date: '', end_date: '' },
    {},
    false,
  );

  /* ========================================================
   * SECTION ORDER API
   * ========================================================
   */

  const { error: sectionOrderError, mutate: updateSectionOrder } =
    useFetch<TodoResponseType>(
      APIEndpoints.ACTIONDASHBOARD_SECTION_ORDER,
      {},
      {},
      false,
    );

  /* ========================================================
   * DATA
   * ========================================================
   */

  const coverageAvailable = dashboardData?.data.coverage_available;

  const actionRequired = dashboardData?.data.action_required;

  const specialtyPharmacy = dashboardData?.data.specialty_pharmacy;

  const pending = dashboardData?.data.pending;

  /* ========================================================
   * API SECTION ORDER
   * ========================================================
   */

  const apiSectionOrder = useMemo(
    () => resolveSectionOrder(dashboardData?.data.section_order),
    [dashboardData?.data.section_order],
  );

  /* ========================================================
   * SAVE SECTION ORDER
   * ========================================================
   */

  const saveSectionOrder = useCallback(
    (order: DashboardSectionId[]): void => {
      updateSectionOrder({
        section_order: order,
      });
    },
    [updateSectionOrder],
  );

  useEffect(() => {
    saveOrderRef.current = saveSectionOrder;
  }, [saveSectionOrder]);

  /* ========================================================
   * REFRESH GRID HEIGHTS
   * ========================================================
   */

  const scheduleGridRefresh = useCallback((grid: GridStack): void => {
    if (refreshFrameRef.current !== null) {
      window.cancelAnimationFrame(refreshFrameRef.current);

      refreshFrameRef.current = null;
    }

    if (secondRefreshFrameRef.current !== null) {
      window.cancelAnimationFrame(secondRefreshFrameRef.current);

      secondRefreshFrameRef.current = null;
    }

    refreshFrameRef.current = window.requestAnimationFrame(() => {
      refreshFrameRef.current = null;

      secondRefreshFrameRef.current = window.requestAnimationFrame(() => {
        secondRefreshFrameRef.current = null;

        if (gridInstanceRef.current !== grid || !isGridActive(grid)) {
          return;
        }

        /*
         * Measure after React restores the body, reapply the order using
         * the new heights, then measure once more after load().
         */
        grid.getGridItems().forEach((item) => {
          grid.resizeToContent(item);
        });

        applySectionOrder(grid, currentOrderRef.current);

        grid.getGridItems().forEach((item) => {
          grid.resizeToContent(item);
        });

        grid.compact("list");
      });
    });
  }, []);

  /* ========================================================
   * COLLAPSE / EXPAND
   * ========================================================
   */

  const toggleSectionCollapse = useCallback((id: DashboardSectionId) => {
    setCollapsedSections((previous) => ({
      ...previous,

      [id]: !previous[id],
    }));
  }, []);

  /*
   * When section body visibility changes,
   * GridStack needs to calculate the new card height.
   */
  useEffect(() => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    scheduleGridRefresh(grid);
  }, [collapsedSections, scheduleGridRefresh]);

  /* ========================================================
   * INITIALIZE GRIDSTACK
   * ========================================================
   */

  useEffect(() => {
    const container = gridContainerRef.current;

    if (!container || gridInstanceRef.current) {
      return;
    }

    const initializedGrid = GridStack.init(
      {
        column: 1,
        cellHeight: 10,
        margin: 12,
        disableResize: true,
        float: false,
        animate: true,
        sizeToContent: true,

        /*
         * Very important:
         *
         * ONLY grip button is draggable.
         *
         * Collapse arrow is NOT draggable.
         */
        handle: ".action-dashboard-drag-handle",

        placeholderClass: "grid-stack-placeholder",
      },

      container,
    );

    if (!initializedGrid) {
      console.error("GridStack initialization failed");

      return;
    }

    const grid: GridStack = initializedGrid;

    gridInstanceRef.current = grid;

    /*
     * Make GridStack use same IDs as API section_order.
     */
    grid.getGridItems().forEach((item) => {
      const id = item.dataset.gridId;

      if (isDashboardSectionId(id)) {
        grid.update(item, {
          id,
          x: 0,
          w: 1,
          noResize: true,
          sizeToContent: true,
        });
      }
    });

    applySectionOrder(grid, currentOrderRef.current);

    scheduleGridRefresh(grid);

    /* ======================================================
     * DRAG STOP
     * ======================================================
     */

    const handleDragStop = (
      _event: Event,
      draggedElement: GridItemHTMLElement,
    ): void => {
      if (gridInstanceRef.current !== grid || !isGridActive(grid)) {
        return;
      }

      const positions = getSectionPositions(grid);

      if (positions.length !== DEFAULT_SECTION_ORDER.length) {
        return;
      }

      const order = positions.map(({ id }) => id);

      currentOrderRef.current = order;

      console.log("Dragged section:", draggedElement.dataset.gridId);

      console.log("Updated section order:", order);

      console.table(positions);

      saveOrderRef.current(order);

      scheduleGridRefresh(grid);
    };

    grid.on("dragstop", handleDragStop);

    /* ======================================================
     * CLEANUP
     * ======================================================
     */

    return () => {
      if (refreshFrameRef.current !== null) {
        window.cancelAnimationFrame(refreshFrameRef.current);

        refreshFrameRef.current = null;
      }

      if (secondRefreshFrameRef.current !== null) {
        window.cancelAnimationFrame(secondRefreshFrameRef.current);

        secondRefreshFrameRef.current = null;
      }

      if (gridInstanceRef.current === grid) {
        gridInstanceRef.current = null;
      }

      if (isGridActive(grid)) {
        grid.off("dragstop");

        grid.destroy(false);
      }
    };
  }, [scheduleGridRefresh]);

  /* ========================================================
   * APPLY API SECTION ORDER
   * ========================================================
   */

  useEffect(() => {
    currentOrderRef.current = apiSectionOrder;

    console.log(
      "TodoResponse section_order:",
      dashboardData?.data.section_order,
    );

    console.log("Resolved section order:", apiSectionOrder);

    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    applySectionOrder(grid, apiSectionOrder);

    scheduleGridRefresh(grid);
  }, [apiSectionOrder, dashboardData?.data.section_order, scheduleGridRefresh]);

  /* ========================================================
   * LOAD DASHBOARD
   * ========================================================
   */

  useEffect(() => {
    const refType = userData?.ref_type ?? "";

    const { start_date, end_date, ...cleanedQueries } = queryParams;

    /*
     * CONTACT / PRACTICE
     */
    if (["Contact", "Practice"].includes(refType)) {
      const practiceId = practiceDropdownApiRes?.data?.[0]?.id?.toString();

      if (!practiceId) {
        return;
      }

      dispatch({
        type: PatientFilterStateActions.RESET_FILTERS,

        payload: {
          practice_id: practiceId,
        },
      });

      updateDataWithQueryParams(
        {},
        {},
        {
          ...cleanedQueries,
          start_date: '',
          end_date: '',
          practice_id: practiceId,
        },
      );

      return;
    }

    /*
     * ORGANIZATION
     */
    if (refType === "Organization") {
      updateDataWithQueryParams(
        {},
        {},
        {
          ...cleanedQueries,
          start_date: '',
          end_date: '',
        },
      );

      return;
    }

    /*
     * GLOBAL DOCTOR
     */
    if (refType === "GlobalDoctor") {
      const globalDoctorId = userData?.ref_id?.toString() ?? "";

      dispatch({
        type: PatientFilterStateActions.RESET_FILTERS,

        payload: {
          global_doctor_id: globalDoctorId,
        },
      });

      updateDataWithQueryParams(
        {},
        {},
        {
          ...cleanedQueries,
          start_date: '',
          end_date: '',
          global_doctor_id: globalDoctorId,
        },
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceDropdownApiRes, userData?.ref_id, userData?.ref_type]);

  /* ========================================================
   * API RESPONSE
   * ========================================================
   */

  useEffect(() => {
    if (dashboardData) {
      hideLoader();

      showToast(["Todo's data fetched successfully"], ToastTypes.SUCCESS);
    }

    if (dashboardError) {
      hideLoader();
    }

    if (sectionOrderError) {
      showToast(["Unable to update dashboard section order"], ToastTypes.ERROR);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData, dashboardError, sectionOrderError]);

  /* ========================================================
   * SEARCH DASHBOARD
   * ========================================================
   */

  const searchDashboard = (
    updatedState: ActionDashboardState = queryParams,
  ): void => {
    const params = new URLSearchParams(
      updatedState as unknown as Record<string, string>,
    );

    updateSearchParams(params.toString(), {
      replace: false,
    });

    setLoader();

    updateDataWithQueryParams(null, null, {
      ...updatedState,
      start_date: "",
      end_date: "",
    });
  };

  /* ========================================================
   * PAGE CHANGE
   * ========================================================
   */

  const changePageNumber = (
    action: ActionStatisticsDashboardActionType,
  ): void => {
    const updatedState = tableFilterReducer(queryParams, action);

    dispatch(action);

    searchDashboard(updatedState);
  };

  /* ========================================================
   * RESET SECTION ORDER
   * ========================================================
   */

  const resetDashboardLayout = (): void => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    const defaultOrder = [...DEFAULT_SECTION_ORDER];

    currentOrderRef.current = defaultOrder;

    applySectionOrder(grid, defaultOrder);

    scheduleGridRefresh(grid);

    saveOrderRef.current(defaultOrder);
  };

  /* ========================================================
   * ORGANIZATION URL FILTERS
   * ========================================================
   */

  useEffect(() => {
    if (userData?.ref_type !== "Organization") {
      return;
    }

    const params = new URLSearchParams(
      queryParams as unknown as Record<string, string>,
    );

    updateSearchParams(params.toString(), {
      replace: false,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, userData?.ref_type]);

  /* ========================================================
   * RENDER SECTION
   * ========================================================
   */

  const renderSection = (id: DashboardSectionId): JSX.Element => {
    switch (id) {
      /* ====================================================
       * COVERAGE AVAILABLE
       * ====================================================
       */

      case "coverage_available":
        return (
          <GridSection
            key={id}
            id={id}
            title="Coverage Available"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <div className="bg-primary-dark p-3">
              <p className="mb-0 text-dark fs-6">
                These are patients that have either medical coverage available
                and/or pharmacy coverage available. Please provide patient
                status update when a treatment has been completed or not.
              </p>

              <p className="mb-0 text-dark fs-6">
                For Case Type, 'A' indicates an automatically rerun case, 'M'
                indicates a manually re-run case, and 'New' indicates a case for
                a new patient. <i className="fas fa-comment" /> indicates cases
                with new notes.
              </p>
            </div>

            <CoverageAvailable
              tableData={coverageAvailable}
              currentPage={queryParams.treatable_page}
              onPageChange={changePageNumber}
              onActionPerformed={() =>
                updateDataWithQueryParams({}, {}, {
                  ...queryParams,
                  start_date: "",
                  end_date: "",
                })
              }
            />
          </GridSection>
        );

      /* ====================================================
       * ACTION REQUIRED
       * ====================================================
       */

      case "action_required":
        return (
          <GridSection
            key={id}
            id={id}
            title="Action Required"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <div className="custom-bg-light-purple p-2">
              <p className="mb-0 text-dark fs-6">
                This is a list of all cases requiring your attention before a
                treatment decision should be determined. Click on the
                appropriate next steps to proceed.
              </p>

              <p className="mb-0 text-dark fs-6">
                For Case Type, 'A' indicates an automatically rerun case, 'M'
                indicates a manually re-run case, and 'New' indicates a case for
                a new patient. <i className="fas fa-comment" /> indicates cases
                with new notes.
              </p>
            </div>

            <ActionRequired
              tableData={actionRequired}
              currentPage={queryParams.todo_page}
              onPageChange={changePageNumber}
              onActionPerformed={() =>
                updateDataWithQueryParams({}, {}, {
                  ...queryParams,
                  start_date: "",
                  end_date: "",
                })
              }
            />
          </GridSection>
        );

      /* ====================================================
       * SPECIALTY PHARMACY
       * ====================================================
       */

      case "specialty_pharmacy":
        return (
          <GridSection
            key={id}
            id={id}
            title="Transferred to Specialty Pharmacy"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <div className="custom-bg-light-purple p-2">
              <p className="mb-0 text-dark fs-6">
                This is a list of patients awaiting specialty pharmacy shipment
                to your office. Please provide patient status update when a
                treatment has been completed or not.{" "}
                <i className="fas fa-comment" /> indicates cases with new notes.
              </p>
            </div>

            <SpecialityPharmacy
              tableData={specialtyPharmacy}
              currentPage={queryParams.sp_treatment_page}
              onPageChange={changePageNumber}
              onActionPerformed={() =>
                updateDataWithQueryParams({}, {}, {
                  ...queryParams,
                  start_date: "",
                  end_date: "",
                })
              }
            />
          </GridSection>
        );

      /* ====================================================
       * PENDING
       * ====================================================
       */

      case "pending":
        return (
          <GridSection
            key={id}
            id={id}
            title="Pending"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <div className="custom-bg-light-purple p-2">
              <p className="mb-0 text-dark fs-6">
                This is a list of cases with no or indeterminate coverage. Click
                on the appropriate patient status to update the case.
              </p>

              <p className="mb-0 text-dark fs-6">
                For Case Type, 'A' indicates an automatically rerun case, 'M'
                indicates a manually re-run case, and 'New' indicates a case for
                a new patient. <i className="fas fa-comment" /> indicates cases
                with new notes.
              </p>
            </div>

            <PendingTable
              tableData={pending}
              currentPage={queryParams.not_treated_page}
              onPageChange={changePageNumber}
              onActionPerformed={() =>
               updateDataWithQueryParams({}, {}, {
                  ...queryParams,
                  start_date: "",
                  end_date: "",
                })
              }
            />
          </GridSection>
        );
    }
  };

  /* ========================================================
   * RENDER
   * ========================================================
   */

  return (
    <div className="container-fluid cases-page overflow-y-auto overflow-x-auto d-flex flex-column flex-fill">
      <AuthSubHeader />

      {/* ===================================================
            RESET ORDER
        =================================================== */}

      <div className="d-flex justify-content-end mt-3 me-3">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm action-dashboard-reset-button"
          onClick={resetDashboardLayout}
        >
          <i className="fas fa-undo-alt me-2" aria-hidden="true" />
          Reset Section Order
        </button>
      </div>

      {/* ===================================================
            GRID
        =================================================== */}

      <div className="d-flex flex-column">
        <div
          ref={gridContainerRef}
          className="grid-stack action-dashboard-grid mt-2"
        >
          {DEFAULT_SECTION_ORDER.map(renderSection)}
        </div>
      </div>

      <FooterText/>
    </div>
  );
};

export default ActionDashboardListing;
